/**
 * ComparisonView
 *
 * Main comparison view showing before/after exercise data.
 * Used in AIImportDialog when comparison toggle is enabled.
 */

import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Alert,
  Grid
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { Exercice, Question } from '../../types';
import FieldComparison from './FieldComparison';
import QuestionComparison from './QuestionComparison';

// ============================================================================
// TYPES
// ============================================================================

interface ComparisonViewProps {
  before: Exercice;
  after: Partial<Omit<Exercice, 'id' | 'chapitreId' | 'leconId'>>;
  onConfirm: () => void;
  onBack: () => void;
}

interface ChangeSummary {
  modifiedFields: number;
  addedQuestions: number;
  removedQuestions: number;
  modifiedQuestions: number;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Calculate summary of changes
 */
function calculateChangeSummary(
  before: Exercice,
  after: Partial<Omit<Exercice, 'id' | 'chapitreId' | 'leconId'>>
): ChangeSummary {
  let modifiedFields = 0;

  // Check simple fields
  if (after.titre !== undefined && after.titre !== before.titre) modifiedFields++;
  if (after.description !== undefined && after.description !== before.description) modifiedFields++;
  if (after.niveau !== undefined && after.niveau !== before.niveau) modifiedFields++;
  if (after.tempsEstimeMinutes !== undefined && after.tempsEstimeMinutes !== before.tempsEstimeMinutes) modifiedFields++;

  // Check competencesVisees
  if (after.competencesVisees !== undefined) {
    const beforeComps = before.competencesVisees || [];
    const afterComps = after.competencesVisees;
    if (beforeComps.length !== afterComps.length || beforeComps.some((c, i) => c !== afterComps[i])) {
      modifiedFields++;
    }
  }

  // Check questions
  const beforeQuestions = before.questions || [];
  const afterQuestions = (after.questions as Question[]) || beforeQuestions;

  let addedQuestions = 0;
  let removedQuestions = 0;
  let modifiedQuestions = 0;

  const maxLen = Math.max(beforeQuestions.length, afterQuestions.length);
  for (let i = 0; i < maxLen; i++) {
    const bq = beforeQuestions[i];
    const aq = afterQuestions[i];

    if (!bq && aq) {
      addedQuestions++;
    } else if (bq && !aq) {
      removedQuestions++;
    } else if (bq && aq) {
      // Check if modified
      if (
        bq.texte !== aq.texte ||
        bq.type !== aq.type ||
        bq.reponseCorrecte !== aq.reponseCorrecte ||
        bq.explication !== aq.explication ||
        bq.points !== aq.points
      ) {
        modifiedQuestions++;
      }
    }
  }

  return {
    modifiedFields,
    addedQuestions,
    removedQuestions,
    modifiedQuestions
  };
}

// ============================================================================
// COMPONENT
// ============================================================================

const ComparisonView: React.FC<ComparisonViewProps> = ({
  before,
  after,
  onConfirm,
  onBack
}) => {
  const summary = useMemo(() => calculateChangeSummary(before, after), [before, after]);

  const totalChanges =
    summary.modifiedFields +
    summary.addedQuestions +
    summary.removedQuestions +
    summary.modifiedQuestions;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">
                AVANT (Exercice actuel)
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'primary.lighter', textAlign: 'center' }}>
              <Typography variant="subtitle2" color="primary.main">
                APRES (Donnees importees)
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Comparison content */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
        {/* Titre */}
        <FieldComparison
          label="TITRE"
          beforeValue={before.titre}
          afterValue={after.titre ?? before.titre}
          type="text"
        />

        {/* Description */}
        <FieldComparison
          label="DESCRIPTION"
          beforeValue={before.description}
          afterValue={after.description ?? before.description}
          type="text"
        />

        {/* Niveau */}
        <FieldComparison
          label="NIVEAU"
          beforeValue={before.niveau}
          afterValue={after.niveau ?? before.niveau}
          type="niveau"
        />

        {/* Temps estime */}
        <FieldComparison
          label="TEMPS ESTIME"
          beforeValue={before.tempsEstimeMinutes}
          afterValue={after.tempsEstimeMinutes ?? before.tempsEstimeMinutes}
          type="number"
        />

        {/* Competences visees */}
        <FieldComparison
          label="COMPETENCES VISEES"
          beforeValue={before.competencesVisees}
          afterValue={after.competencesVisees ?? before.competencesVisees}
          type="array"
        />

        {/* Questions */}
        <QuestionComparison
          beforeQuestions={before.questions || []}
          afterQuestions={(after.questions as Question[]) || before.questions || []}
        />
      </Box>

      <Divider />

      {/* Summary */}
      <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>
          Resume des modifications
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {summary.modifiedFields > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EditIcon fontSize="small" color="warning" />
              <Typography variant="body2">
                {summary.modifiedFields} champ{summary.modifiedFields > 1 ? 's' : ''} modifie{summary.modifiedFields > 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
          {summary.modifiedQuestions > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EditIcon fontSize="small" color="warning" />
              <Typography variant="body2">
                {summary.modifiedQuestions} question{summary.modifiedQuestions > 1 ? 's' : ''} modifiee{summary.modifiedQuestions > 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
          {summary.addedQuestions > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AddIcon fontSize="small" color="success" />
              <Typography variant="body2">
                {summary.addedQuestions} question{summary.addedQuestions > 1 ? 's' : ''} ajoutee{summary.addedQuestions > 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
          {summary.removedQuestions > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RemoveIcon fontSize="small" color="error" />
              <Typography variant="body2">
                {summary.removedQuestions} question{summary.removedQuestions > 1 ? 's' : ''} supprimee{summary.removedQuestions > 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
          {totalChanges === 0 && (
            <Alert severity="info" sx={{ py: 0 }}>
              Aucune modification detectee
            </Alert>
          )}
        </Box>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
        <Button
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          color="inherit"
        >
          Retour
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          startIcon={<CheckIcon />}
        >
          Confirmer l'import
        </Button>
      </Box>
    </Box>
  );
};

export default ComparisonView;
